// src/pages/peta-estate-4/contexts/MapDataContext.jsx
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { ref as dbRef, onValue, off } from 'firebase/database';
import { database } from '../../../firebase'; // Ensure Firebase database instance is imported

// Create the Context
export const MapDataContext = createContext();

// Create the Provider component
export const MapDataProvider = ({ children }) => {
  // State to store GeoJSON data from Realtime Database
  const [geojsonFeaturesLokasi, setGeojsonFeaturesLokasi] = useState([]);
  const [geojsonFeaturesJalanUtama, setGeojsonFeaturesJalanUtama] = useState([]);
  const [geojsonFeaturesLebungArea, setGeojsonFeaturesLebungArea] = useState([]);
  const [geojsonFeaturesPipaGas, setGeojsonFeaturesPipaGas] = useState([]);
  const [geojsonFeaturesPosKeamanan, setGeojsonFeaturesPosKeamanan] = useState([]);

  const [pineappleData, setPineappleData] = useState({});
  const [bananaData, setBananaData] = useState({});

  // State to store status color mapping (Realtime Database)
  const [statusColors, setStatusColors] = useState({});
  // State for real-time foreman locations
  const [mandorLocations, setMandorLocations] = useState([]);

  // Loading states
  const [loadingGeoJsonLokasi, setLoadingGeoJsonLokasi] = useState(true);
  const [loadingGeoJsonJalanUtama, setLoadingGeoJsonJalanUtama] = useState(true);
  const [loadingGeoJsonLebungArea, setLoadingGeoJsonLebungArea] = useState(true);
  const [loadingGeoJsonPipaGas, setLoadingGeoJsonPipaGas] = useState(true);
  const [loadingGeoJsonPosKeamanan, setLoadingGeoJsonPosKeamanan] = useState(true);
  const [loadingPineapple, setLoadingPineapple] = useState(true);
  const [loadingBanana, setLoadingBanana] = useState(true);
  const [loadingStatusColors, setLoadingStatusColors] = useState(true);
  const [loadingMandor, setLoadingMandor] = useState(true);

  // State to store error messages
  const [error, setError] = useState(null);

  // Master loading status
  const masterLoading = loadingGeoJsonLokasi || loadingPineapple || loadingBanana || 
                        loadingStatusColors || loadingMandor || loadingGeoJsonJalanUtama || 
                        loadingGeoJsonLebungArea || loadingGeoJsonPipaGas || loadingGeoJsonPosKeamanan;

  useEffect(() => {
    if (!database) {
      setError("Firebase Realtime Database instance is not available.");
      // Set semua loading ke false
      [setLoadingGeoJsonLokasi, setLoadingGeoJsonJalanUtama, setLoadingGeoJsonLebungArea,
       setLoadingGeoJsonPipaGas, setLoadingGeoJsonPosKeamanan, setLoadingPineapple,
       setLoadingBanana, setLoadingStatusColors, setLoadingMandor].forEach(setter => setter(false));
      return;
    }

    // Helper function to validate GeoJSON features
    const isValidGeoJSONFeature = (feature) => {
      return feature && feature.geometry && feature.geometry.coordinates;
    };

    // --- Fetch GeoJSON Lokasi features ---
    const geojsonLokasiDbRef = dbRef(database, `db_peta/tb_geojson/tb_lokasi`); 
    const unsubscribeGeojsonLokasi = onValue(geojsonLokasiDbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const featuresArray = Object.values(data).filter(isValidGeoJSONFeature); // Filter invalid features
        setGeojsonFeaturesLokasi({
          type: "FeatureCollection",
          features: featuresArray
        });
      } else {
        setGeojsonFeaturesLokasi({ type: "FeatureCollection", features: [] });
      }
      setLoadingGeoJsonLokasi(false); // Mark GeoJSON Lokasi loading as complete
    }, (dbError) => {
      console.error("Error fetching GeoJSON Lokasi data from RTDB:", dbError);
      setError("Failed to load GeoJSON Lokasi data from Realtime Database.");
      setLoadingGeoJsonLokasi(false); // Mark GeoJSON Lokasi loading as complete even with error
    });

    // --- Fetch GeoJSON Jalan Utama features ---
    const geojsonJalanUtamaDbRef = dbRef(database, `db_peta/tb_geojson/tb_jalan_utama`); 
    const unsubscribeGeojsonJalanUtama = onValue(geojsonJalanUtamaDbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const featuresArray = Object.values(data).filter(isValidGeoJSONFeature); // Filter invalid features
        setGeojsonFeaturesJalanUtama({
          type: "FeatureCollection",
          features: featuresArray
        });
      } else {
        setGeojsonFeaturesJalanUtama({ type: "FeatureCollection", features: [] });
      }
      setLoadingGeoJsonJalanUtama(false); 
    }, (dbError) => {
      console.error("Error fetching GeoJSON Jalan Utama data from RTDB:", dbError);
      setLoadingGeoJsonJalanUtama(false); 
    });

    // --- Fetch GeoJSON Lebung Area features ---
    const geojsonLebungAreaDbRef = dbRef(database, `db_peta/tb_geojson/tb_lebung_area`); 
    const unsubscribeGeojsonLebungArea = onValue(geojsonLebungAreaDbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const featuresArray = Object.values(data).filter(isValidGeoJSONFeature); // Filter invalid features
        setGeojsonFeaturesLebungArea({
          type: "FeatureCollection",
          features: featuresArray
        });
      } else {
        setGeojsonFeaturesLebungArea({ type: "FeatureCollection", features: [] });
      }
      setLoadingGeoJsonLebungArea(false); 
    }, (dbError) => {
      console.error("Error fetching GeoJSON Lebung Area data from RTDB:", dbError);
      setLoadingGeoJsonLebungArea(false); 
    });

    // --- Fetch GeoJSON Pipa Gas features ---
    const geojsonPipaGasDbRef = dbRef(database, `db_peta/tb_geojson/tb_pipa_gas`); 
    const unsubscribeGeojsonPipaGas = onValue(geojsonPipaGasDbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const featuresArray = Object.values(data).filter(isValidGeoJSONFeature); // Filter invalid features
        setGeojsonFeaturesPipaGas({
          type: "FeatureCollection",
          features: featuresArray
        });
      } else {
        setGeojsonFeaturesPipaGas({ type: "FeatureCollection", features: [] });
      }
      setLoadingGeoJsonPipaGas(false); 
    }, (dbError) => {
      console.error("Error fetching GeoJSON Pipa Gas data from RTDB:", dbError);
      setLoadingGeoJsonPipaGas(false); 
    });

    // --- Fetch GeoJSON Pos Keamanan features ---
    const geojsonPosKeamananDbRef = dbRef(database, `db_peta/tb_geojson/tb_pos_keamanan`); 
    const unsubscribeGeojsonPosKeamanan = onValue(geojsonPosKeamananDbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const featuresArray = Object.values(data).filter(isValidGeoJSONFeature); // Filter invalid features
        setGeojsonFeaturesPosKeamanan({
          type: "FeatureCollection",
          features: featuresArray
        });
      } else {
        setGeojsonFeaturesPosKeamanan({ type: "FeatureCollection", features: [] });
      }
      setLoadingGeoJsonPosKeamanan(false); 
    }, (dbError) => {
      console.error("Error fetching GeoJSON Pos Keamanan data from RTDB:", dbError);
      setLoadingGeoJsonPosKeamanan(false); 
    });


    // --- Fetch Pineapple data ---
    const pineappleRef = dbRef(database, `db_freshpine/tb_lokasi`);
    const unsubscribePineapple = onValue(pineappleRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const transformedData = Object.values(data).reduce((acc, curr) => {
          if (curr.lokasi && curr.status_plantation === 'active') { 
            acc[curr.lokasi] = curr;
          }
          return acc;
        }, {});
        setPineappleData(transformedData);
      } else {
        setPineappleData({});
      }
      setLoadingPineapple(false);
    }, (dbError) => {
      console.error("Error fetching pineapple data:", dbError);
      setError("Failed to load pineapple data.");
      setLoadingPineapple(false);
    });

// --- Fetch Banana data ---
const bananaRef = dbRef(database, `db_banana/tb_penyakit`);
// Di MapDataContext.jsx - GANTI dengan:
const unsubscribeBanana = onValue(bananaRef, (snapshot) => {
  const data = snapshot.val();
  if (data) {
    // Struktur yang benar: key adalah lokasi, value adalah array of records
    const transformedData = Object.values(data).reduce((acc, curr) => {
      if (curr.lokasi) {
        const normalizedLokasi = curr.lokasi.toString().trim().toUpperCase();
        
        if (!acc[normalizedLokasi]) {
          acc[normalizedLokasi] = [];
        }
        
        // Di MapDataContext.jsx - pastikan data penyakit diambil dengan benar
acc[normalizedLokasi].push({
  ...curr,
  tahun: curr.tahun || '',
  status: curr.status || '',
  hill: curr.hill || '',
  jenis_penyakit: {
    panama_disease: curr.jenis_penyakit?.panama_disease || '0.00',
    moko: curr.jenis_penyakit?.moko || '0.00',
    tip_over: curr.jenis_penyakit?.tip_over || '0.00',
    blowdown: curr.jenis_penyakit?.blowdown || '0.00'
  }
});
      }
      return acc;
    }, {});
    
    setBananaData(transformedData);
  } else {
    setBananaData({});
  }
  setLoadingBanana(false);
}, (dbError) => {
  console.error("Error fetching banana data:", dbError);
  setError("Failed to load banana data.");
  setLoadingBanana(false);
});

    // --- Fetch status colors ---
    const statusColorsRef = dbRef(database, `db_config/db_fresh_pineapple/tb_status_lokasi`);
    const unsubscribeStatusColors = onValue(statusColorsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const colors = Object.values(data).reduce((acc, curr) => {
          if (curr.nama_status_lokasi && curr.indicator) {
            acc[curr.nama_status_lokasi] = curr.indicator;
          }
          return acc;
        }, {});
        setStatusColors(colors);
      } else {
        setStatusColors({});
      }
      setLoadingStatusColors(false); // Mark status colors loading as complete
    }, (dbError) => {
      console.error("Error fetching status colors:", dbError);
      setError("Failed to load status color data.");
      setLoadingStatusColors(false); // Mark status colors loading as complete even with error
    });

    // --- Fetch foreman locations ---
    const mandorLocationRef = dbRef(database, `mandor_locations`); 
    const unsubscribeMandor = onValue(mandorLocationRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const locations = Object.entries(data).map(([id, loc]) => ({
          id,
          lat: loc.lat,
          lng: loc.lng,
          name: loc.name || `Mandor ${id}`, 
          timestamp: loc.timestamp
        }));
        setMandorLocations(locations);
      } else {
        setMandorLocations([]);
      }
      setLoadingMandor(false); // Mark mandor loading as complete
    }, (dbError) => {
      console.error("Error fetching mandor locations:", dbError);
      setLoadingMandor(false); // Mark mandor loading as complete even with error
    });

    // Cleanup function
    return () => {
      // Unsubscribe semua listener GeoJSON
      unsubscribeGeojsonLokasi();
      unsubscribeGeojsonJalanUtama(); 
      unsubscribeGeojsonLebungArea(); 
      unsubscribeGeojsonPipaGas(); 
      unsubscribeGeojsonPosKeamanan(); 
      
      // Unsubscribe data listeners
      unsubscribePineapple();
      unsubscribeBanana();
      unsubscribeStatusColors();
      unsubscribeMandor();
    };
  }, []);

  return (
    <MapDataContext.Provider value={{
      geojsonFeaturesLokasi,
      geojsonFeaturesJalanUtama,
      geojsonFeaturesLebungArea,
      geojsonFeaturesPipaGas,
      geojsonFeaturesPosKeamanan,
      pineappleData,    // Ganti plantationData menjadi pineappleData
      bananaData,       // Tambahkan bananaData
      statusColors,
      mandorLocations,
      loading: masterLoading,
      error
    }}>
      {children}
    </MapDataContext.Provider>
  );
};